/**
 * UnitEconomicsAdminPage — editable admin table for unit economics inputs.
 * Change tier prices, infra costs, retention, mix %, global CAC, subs, opex, etc.
 * Values are auto-calculated in real time and displayed alongside the inputs.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  RotateCcw, Plus, Trash2, Calculator, TrendingUp,
  DollarSign, Users, Settings,
} from "lucide-react";
import PageBackButton from "../components/PageBackButton";
import {
  UnitEconomicsProvider,
  useUnitEconomics,
} from "../contexts/UnitEconomicsContext";

// ── Reusable number input ────────────────────────────────────────────────────
function NumField({ label, value, onChange, prefix, suffix, min = 0, step = 1, className = "" }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label className="text-xs text-gray-400 font-medium uppercase tracking-wider">{label}</label>
      <div className="flex items-center gap-1 bg-[#0A0F1A] border border-white/10 rounded-lg px-3 py-2">
        {prefix && <span className="text-gray-500 text-sm">{prefix}</span>}
        <input
          type="number"
          value={value}
          min={min}
          step={step}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="bg-transparent text-white text-sm w-full outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        {suffix && <span className="text-gray-500 text-sm">{suffix}</span>}
      </div>
    </div>
  );
}

// ── Main inner component (needs context) ─────────────────────────────────────
function UEAdminInner() {
  const { inputs, computed, updateGlobals, updateTier, addTier, removeTier, resetAll } = useUnitEconomics();
  const [showRaw, setShowRaw] = useState(false);

  const { headline, _raw: raw } = computed;

  return (
    <div className="min-h-screen bg-[#0A0F1A] text-white">
      {/* ── Top bar ── */}
      <div className="sticky top-0 z-30 bg-[#0A0F1A]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <PageBackButton label="Back to Visual Settings" to="/invest/admin" preferHistory={false} />
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Calculator size={20} className="text-[#4FA9A1]" />
                Unit Economics Editor
              </h1>
              <p className="text-xs text-gray-400 mt-0.5">
                Change inputs below — outputs update in real time
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/invest"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-white/5 hover:bg-white/10 transition-colors"
            >
              <TrendingUp size={14} /> Preview
            </Link>
            <Link
              to="/invest/admin"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-white/5 hover:bg-white/10 transition-colors"
            >
              <Settings size={14} /> Visual Settings
            </Link>
            <button
              onClick={resetAll}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
            >
              <RotateCcw size={14} /> Reset
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-10">
        {/* ── Global inputs ── */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <DollarSign size={18} className="text-[#3B82F6]" />
            Global Parameters
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <NumField
              label="Projection Month"
              value={inputs.month}
              onChange={(v) => updateGlobals({ month: v })}
              suffix="mo"
              min={1}
            />
            <NumField
              label="Total Subscribers"
              value={inputs.totalSubs}
              onChange={(v) => updateGlobals({ totalSubs: v })}
              prefix="#"
              step={10}
            />
            <NumField
              label="CAC"
              value={inputs.cac}
              onChange={(v) => updateGlobals({ cac: v })}
              prefix={inputs.currency}
              step={5}
            />
            <NumField
              label="Monthly OpEx"
              value={inputs.monthlyOpex}
              onChange={(v) => updateGlobals({ monthlyOpex: v })}
              prefix={inputs.currency}
              step={1000}
            />
            <NumField
              label="Starting Cash"
              value={inputs.startingCash}
              onChange={(v) => updateGlobals({ startingCash: v })}
              prefix={inputs.currency}
              step={10000}
            />
            <NumField
              label="Currency Symbol"
              value={inputs.currency}
              onChange={() => {}}
              className="hidden"
            />
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-400 font-medium uppercase tracking-wider">Currency</label>
              <div className="flex items-center gap-1 bg-[#0A0F1A] border border-white/10 rounded-lg px-3 py-2">
                <input
                  type="text"
                  value={inputs.currency}
                  onChange={(e) => updateGlobals({ currency: e.target.value })}
                  className="bg-transparent text-white text-sm w-full outline-none"
                  maxLength={5}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── Tier inputs ── */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Users size={18} className="text-[#A78BFA]" />
              Tier Configuration
            </h2>
            <button
              onClick={addTier}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-[#3B82F6]/20 text-[#60A5FA] hover:bg-[#3B82F6]/30 transition-colors"
            >
              <Plus size={14} /> Add Tier
            </button>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-white/10">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0F1826]/80 text-left">
                  <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Tier Name</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Price/mo</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Infra Cost</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Retention</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Mix %</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">GM$/mo</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">LTV (GM$)</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">LTV:CAC</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">Subs</th>
                  <th className="px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {inputs.tiers.map((tier, i) => {
                  const derived = raw.tierRows[i];
                  return (
                    <tr key={i} className="border-t border-white/5">
                      {/* Name */}
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={tier.name}
                          onChange={(e) => updateTier(i, { name: e.target.value })}
                          className="bg-[#0A0F1A] border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm w-28 outline-none focus:border-[#3B82F6]/50"
                        />
                      </td>
                      {/* Price */}
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1 bg-[#0A0F1A] border border-white/10 rounded-lg px-2 py-1.5 w-24">
                          <span className="text-gray-500 text-xs">{inputs.currency}</span>
                          <input
                            type="number"
                            value={tier.price}
                            min={0}
                            step={5}
                            onChange={(e) => updateTier(i, { price: parseFloat(e.target.value) || 0 })}
                            className="bg-transparent text-white text-sm w-full outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                          />
                        </div>
                      </td>
                      {/* Infra */}
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1 bg-[#0A0F1A] border border-white/10 rounded-lg px-2 py-1.5 w-24">
                          <span className="text-gray-500 text-xs">{inputs.currency}</span>
                          <input
                            type="number"
                            value={tier.infraCost}
                            min={0}
                            step={1}
                            onChange={(e) => updateTier(i, { infraCost: parseFloat(e.target.value) || 0 })}
                            className="bg-transparent text-white text-sm w-full outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                          />
                        </div>
                      </td>
                      {/* Retention */}
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1 bg-[#0A0F1A] border border-white/10 rounded-lg px-2 py-1.5 w-24">
                          <input
                            type="number"
                            value={tier.retentionMonths}
                            min={1}
                            step={1}
                            onChange={(e) => updateTier(i, { retentionMonths: parseFloat(e.target.value) || 1 })}
                            className="bg-transparent text-white text-sm w-full outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                          />
                          <span className="text-gray-500 text-xs">mo</span>
                        </div>
                      </td>
                      {/* Mix % */}
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1 bg-[#0A0F1A] border border-white/10 rounded-lg px-2 py-1.5 w-20">
                          <input
                            type="number"
                            value={tier.mixPct}
                            min={0}
                            max={100}
                            step={1}
                            onChange={(e) => updateTier(i, { mixPct: parseFloat(e.target.value) || 0 })}
                            className="bg-transparent text-white text-sm w-full outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                          />
                          <span className="text-gray-500 text-xs">%</span>
                        </div>
                      </td>
                      {/* Derived: GM */}
                      <td className="px-4 py-2 text-center">
                        <span className="text-[#4FA9A1] font-medium">
                          {inputs.currency}{derived?.gm?.toFixed(0) ?? "—"}
                        </span>
                      </td>
                      {/* Derived: LTV */}
                      <td className="px-4 py-2 text-center">
                        <span className="text-[#60A5FA] font-medium">
                          {inputs.currency}{derived?.ltv?.toLocaleString("en-AU", { maximumFractionDigits: 0 }) ?? "—"}
                        </span>
                      </td>
                      {/* Derived: LTV:CAC */}
                      <td className="px-4 py-2 text-center">
                        <span className="text-white font-bold">
                          {derived?.ltvCac?.toFixed(1) ?? "—"}×
                        </span>
                      </td>
                      {/* Derived: Subs */}
                      <td className="px-4 py-2 text-center">
                        <span className="text-gray-300">
                          {derived?.subs?.toLocaleString("en-AU") ?? "—"}
                        </span>
                      </td>
                      {/* Delete */}
                      <td className="px-2 py-2 text-center">
                        {inputs.tiers.length > 1 && (
                          <button
                            onClick={() => removeTier(i)}
                            className="p-1.5 rounded-lg text-red-400/60 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            title="Remove tier"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mix % warning */}
          {(() => {
            const sum = inputs.tiers.reduce((s, t) => s + t.mixPct, 0);
            if (Math.abs(sum - 100) > 0.1) {
              return (
                <p className="text-xs text-amber-400 mt-2">
                  ⚠ Mix percentages sum to {sum.toFixed(1)}% — they will be normalised to 100% in calculations.
                </p>
              );
            }
            return null;
          })()}
        </section>

        {/* ── Live computed outputs ── */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp size={18} className="text-[#4FA9A1]" />
            Computed Outputs
            <span className="text-xs text-gray-500 font-normal ml-2">
              (these values populate the pitch deck automatically)
            </span>
          </h2>

          {/* Headline cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: "Subscribers", value: headline.subs },
              { label: "MRR", value: headline.mrr },
              { label: "Gross Margin", value: headline.grossMargin },
              { label: "CAC", value: headline.cac },
              { label: "Payback", value: headline.payback },
              { label: "LTV:CAC", value: headline.ltvCac },
              { label: "Net Monthly", value: headline.netMonthly },
              { label: "Cash Position", value: headline.cashPosition },
            ].map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="bg-[#0F1826]/60 border border-white/8 rounded-xl p-4 text-center"
              >
                <p className="text-xl font-bold text-white">{m.value}</p>
                <p className="text-xs text-gray-400 mt-1">{m.label}</p>
              </motion.div>
            ))}
          </div>

          {/* Weighted averages */}
          <div className="flex flex-wrap gap-6 text-sm mb-6">
            <div>
              <span className="text-gray-400">Wt. ARPU:</span>{" "}
              <span className="text-white font-medium">{computed.weightedArpu}</span>
            </div>
            <div>
              <span className="text-gray-400">Wt. GM:</span>{" "}
              <span className="text-white font-medium">{computed.weightedGm}</span>
            </div>
            <div>
              <span className="text-gray-400">Wt. LTV:</span>{" "}
              <span className="text-white font-bold">{computed.weightedLtv}</span>
            </div>
          </div>

          {/* Raw JSON toggle */}
          <button
            onClick={() => setShowRaw(!showRaw)}
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors mb-2"
          >
            {showRaw ? "Hide" : "Show"} raw JSON
          </button>
          {showRaw && (
            <pre className="bg-[#0F1826] border border-white/8 rounded-xl p-4 text-xs text-gray-400 overflow-x-auto max-h-64">
              {JSON.stringify(raw, null, 2)}
            </pre>
          )}
        </section>
      </div>
    </div>
  );
}

// ── Exported page (wraps in provider) ────────────────────────────────────────
export default function UnitEconomicsAdminPage() {
  return (
    <UnitEconomicsProvider>
      <UEAdminInner />
    </UnitEconomicsProvider>
  );
}
